import { Dispatch, PropsWithChildren, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';

interface DefaultLayoutContextProps {
  isDrawerOpen: boolean;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  menuOptions: MenuItem[];
  setMenuOptions: Dispatch<SetStateAction<MenuItem[]>>;
  containerElementRef: RefObject<HTMLDivElement>;
  isLoadingMenu: boolean;
  setIsLoadingMenu: Dispatch<SetStateAction<boolean>>;
}

export type MenuItem = {
  id: string;
  name: string;
};

const DefaultLayoutContext = createContext<DefaultLayoutContextProps>(undefined!);

export const DefaultLayoutProvider = ({ children }: PropsWithChildren) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isLoadingMenu, setIsLoadingMenu] = useState<boolean>(false);
  const [menuOptions, setMenuOptions] = useState<MenuItem[]>([]);
  const location = useLocation();
  const containerElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location]);

  const values = useMemo(
    () => ({
      isDrawerOpen,
      setIsDrawerOpen,
      menuOptions,
      setMenuOptions,
      containerElementRef,
      isLoadingMenu,
      setIsLoadingMenu,
    }),
    [isDrawerOpen, isLoadingMenu, menuOptions]
  );
  return <DefaultLayoutContext.Provider value={values}>{children}</DefaultLayoutContext.Provider>;
};

export function useDefaultLayoutContext(): DefaultLayoutContextProps {
  const context = useContext(DefaultLayoutContext);

  if (typeof context === 'undefined') {
    throw new Error('useDefaultLayoutContext should be used within the DefaultLayoutContext provider!');
  }

  return context;
}
